import { useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Controller } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { useForm } from 'react-hook-form'
import * as yup from 'yup'
import Button from '@/components/button'
import { Checkbox } from '@/components/checkbox'
import ConfirmDialog from '@/components/confirm-dialog'
import DataTable from '@/components/data-table'
import type { Column } from '@/components/data-table'
import DefinitionList from '@/components/definition-list'
import EmptyState from '@/components/empty-state'
import FormField from '@/components/form-field'
import { Input } from '@/components/input'
import LookupSelect from '@/components/lookup-select'
import PageHeader from '@/components/page-header'
import Select from '@/components/select'
import Skeleton from '@/components/skeleton'
import StatusBadge from '@/components/status-badge'
import Tabs from '@/components/tabs'
import {
  useAtualizarTipoCredencialMutation,
  useCriarTipoCredencialMutation,
} from '@/http/mutations/tipos-credenciais'
import { useCredenciaisQuery } from '@/http/queries/credenciais'
import { useOperadorasLookupQuery } from '@/http/queries/lookups'
import {
  useInterfacesAutorizacaoQuery,
  useTipoCredencialQuery,
} from '@/http/queries/tipos-credenciais'
import type { CredencialResumo } from '@/types/credencial'
import { STATUS_CREDENCIAL } from '@/types/credencial'
import type { LookupItem } from '@/types/lookup'
import type { SalvarTipoCredencialPayload, TipoCredencial } from '@/types/tipo-credencial'
import { useAuth } from '@/utility/context/auth-context'
import { ACAO } from '@/utility/permissions'
import { useDebouncedValue } from '@/utility/use-debounced-value'
import { formatDateDefault, orDash } from '@/utility/Utils'
import styles from './style.module.css'

const schema = yup.object({
  nomeAmigavel: yup.string().trim().required('Preencha o nome amigável').max(255, 'No máximo 255 caracteres'),
  codigoOperadora: yup.string().defined().nullable().default('').max(64, 'No máximo 64 caracteres'),
  interfaceAutorizacaoId: yup.string().required('Selecione a interface de autorização'),
  operadorasId: yup.string().required('Selecione a operadora'),
  hasCpf: yup.boolean().required(),
  hasCnpj: yup.boolean().required(),
  hasTipoLogin: yup.boolean().required(),
  hasUnidadeLogin: yup.boolean().required(),
  authThenExec: yup.boolean().required(),
  hasEligibility: yup.boolean().required(),
  hasCancellation: yup.boolean().required(),
  authorizationInfoAfterExecution: yup.boolean().required(),
  exibirListaGuiaPrincipal: yup.boolean().required(),
  exibirTesteValidacao: yup.boolean().required(),
  limitOfProceduresPerRequest: yup
    .number()
    .transform((valor: number) => (Number.isNaN(valor) ? null : valor))
    .typeError('Informe um número')
    .min(1, 'Deixe vazio para não ter limite, ou informe pelo menos 1')
    .integer('Use um número inteiro')
    .nullable()
    .defined(),
  updateLimitDays: yup
    .number()
    .typeError('Informe um número')
    .min(0, 'Não pode ser negativo')
    .integer('Use um número inteiro')
    .required('Preencha o limite de dias'),
  validadeAssinaturaDias: yup
    .number()
    .transform((valor: number) => (Number.isNaN(valor) ? null : valor))
    .typeError('Informe um número')
    .min(0, 'Não pode ser negativo')
    .integer('Use um número inteiro')
    .nullable()
    .defined(),
  urlPortal: yup.string().defined().nullable().default(''),
  formatoCapturaBiometriaFacial: yup.string().defined().nullable().default(''),
})

type TipoFormValues = yup.InferType<typeof schema>

const FORMATO_BIOMETRIA_OPTIONS = [
  { value: 'image/png', label: 'PNG' },
  { value: 'image/jpeg', label: 'JPEG' },
]

const valoresIniciais = (tipo: TipoCredencial | null): TipoFormValues => ({
  nomeAmigavel: tipo?.nomeAmigavel ?? '',
  codigoOperadora: tipo?.codigoOperadora ?? '',
  interfaceAutorizacaoId: tipo?.interfaceAutorizacaoId ?? '',
  operadorasId: tipo?.operadorasId ?? '',
  hasCpf: tipo?.hasCpf ?? false,
  hasCnpj: tipo?.hasCnpj ?? false,
  hasTipoLogin: tipo?.hasTipoLogin ?? false,
  hasUnidadeLogin: tipo?.hasUnidadeLogin ?? false,
  authThenExec: tipo?.authThenExec ?? false,
  hasEligibility: tipo?.hasEligibility ?? true,
  hasCancellation: tipo?.hasCancellation ?? true,
  authorizationInfoAfterExecution: tipo?.authorizationInfoAfterExecution ?? false,
  exibirListaGuiaPrincipal: tipo?.exibirListaGuiaPrincipal ?? false,
  exibirTesteValidacao: tipo?.exibirTesteValidacao ?? true,
  limitOfProceduresPerRequest: tipo?.limitOfProceduresPerRequest ?? null,
  updateLimitDays: tipo?.updateLimitDays ?? 30,
  validadeAssinaturaDias: tipo?.validadeAssinaturaDias ?? null,
  urlPortal: tipo?.urlPortal ?? '',
  formatoCapturaBiometriaFacial: tipo?.formatoCapturaBiometriaFacial ?? 'image/png',
})

const montarPayload = (dados: TipoFormValues, tipo: TipoCredencial | null): SalvarTipoCredencialPayload => ({
  interfaceAutorizacaoId: dados.interfaceAutorizacaoId,
  operadorasId: dados.operadorasId,
  nomeAmigavel: dados.nomeAmigavel.trim(),
  codigoOperadora: dados.codigoOperadora ? dados.codigoOperadora.trim() : null,
  hasCpf: dados.hasCpf,
  hasCnpj: dados.hasCnpj,
  hasTipoLogin: dados.hasTipoLogin,
  hasUnidadeLogin: dados.hasUnidadeLogin,
  authThenExec: dados.authThenExec,
  hasEligibility: dados.hasEligibility,
  hasCancellation: dados.hasCancellation,
  authorizationInfoAfterExecution: dados.authorizationInfoAfterExecution,
  exibirListaGuiaPrincipal: dados.exibirListaGuiaPrincipal,
  exibirTesteValidacao: dados.exibirTesteValidacao,
  tiposCredencialElegibilityId: tipo?.tiposCredencialElegibilityId ?? null,
  limitOfProceduresPerRequest: dados.limitOfProceduresPerRequest ?? null,
  updateLimitDays: dados.updateLimitDays,
  validadeAssinaturaDias: dados.validadeAssinaturaDias ?? null,
  urlPortal: dados.urlPortal ? dados.urlPortal.trim() : null,
  formatoCapturaBiometriaFacial: dados.formatoCapturaBiometriaFacial || null,
})

const CredenciaisDoTipo = ({ tipoCredencialId }: { tipoCredencialId: string }) => {
  const navigate = useNavigate()
  const [pageNumber, setPageNumber] = useState(1)
  const [pageSize, setPageSize] = useState(20)

  const filtro = useMemo(
    () => ({
      tiposCredenciaisId: tipoCredencialId,
      situacao: STATUS_CREDENCIAL.Todas,
      pageNumber,
      pageSize,
    }),
    [tipoCredencialId, pageNumber, pageSize],
  )

  const { data: page, error, isFetching, refetch } = useCredenciaisQuery(filtro)

  const columns: Column<CredencialResumo>[] = [
    {
      key: 'nome',
      header: 'Credencial',
      render: (credencial) => (
        <div className={styles.stack}>
          <strong>{orDash(credencial.nome)}</strong>
          <span className={styles.muted}>Prestador {orDash(credencial.codigoPrestador)}</span>
        </div>
      ),
    },
    { key: 'cliente', header: 'Cliente', render: (c) => orDash(c.nomeCliente) },
    { key: 'criacao', header: 'Criada em', render: (c) => formatDateDefault(c.createdAt) },
    {
      key: 'situacao',
      header: 'Situação',
      render: (credencial) => (
        <StatusBadge
          label={credencial.ativa ? 'Ativa' : 'Inativa'}
          tone={credencial.ativa ? 'success' : 'neutral'}
        />
      ),
    },
  ]

  return (
    <DataTable
      columns={columns}
      onRowClick={(credencial) => navigate(`/credenciais/${credencial.id}`)}
      page={page}
      getRowKey={(credencial) => credencial.id ?? ''}
      isLoading={isFetching}
      error={error}
      onRetry={() => void refetch()}
      onPageChange={setPageNumber}
      pageSize={pageSize}
      onPageSizeChange={(novoTamanho) => {
        setPageSize(novoTamanho)
        setPageNumber(1)
      }}
      emptyTitle="Nenhuma credencial usa esse tipo"
      emptyDescription="Assim que uma credencial for criada com esse tipo, ela aparece aqui."
    />
  )
}

const TipoCredencialForm = ({
  tipo,
  onCriado,
}: {
  tipo: TipoCredencial | null
  onCriado: (id: string | null) => void
}) => {
  const { can } = useAuth()
  const podeEditar = can(ACAO.EditarTipoCredencial)
  const [dadosPendentes, setDadosPendentes] = useState<TipoFormValues | null>(null)
  const [termoOperadora, setTermoOperadora] = useState('')
  const termoOperadoraDebounced = useDebouncedValue(termoOperadora)

  const interfaces = useInterfacesAutorizacaoQuery()
  const operadoras = useOperadorasLookupQuery(
    termoOperadoraDebounced,
    termoOperadoraDebounced.trim() !== '',
  )

  const {
    control,
    formState: { errors, isDirty },
    handleSubmit,
    register,
    reset,
  } = useForm<TipoFormValues>({
    resolver: yupResolver(schema),
    defaultValues: valoresIniciais(tipo),
  })

  const criar = useCriarTipoCredencialMutation((id) => {
    setDadosPendentes(null)
    onCriado(id)
  })
  const atualizar = useAtualizarTipoCredencialMutation(tipo?.id ?? '', () => {
    if (dadosPendentes) reset(dadosPendentes)
    setDadosPendentes(null)
  })

  const [operadoraEscolhida, setOperadoraEscolhida] = useState<LookupItem | null>(() =>
    tipo?.operadoras?.id
      ? {
          id: tipo.operadoras.id,
          rotulo: tipo.operadoras.nomeFantasia ?? tipo.operadoras.razaoSocial ?? '',
          detalhe: tipo.operadoras.registroAns,
        }
      : null,
  )

  const salvando = criar.isPending || atualizar.isPending

  const opcoesInterface = (interfaces.data ?? []).map((item) => ({
    value: item.id,
    label: item.nome ?? item.id,
  }))
  const interfaceAtualForaDaLista =
    tipo?.interfaceAutorizacaoId &&
    !opcoesInterface.some((opcao) => opcao.value === tipo.interfaceAutorizacaoId)
  if (interfaceAtualForaDaLista) {
    opcoesInterface.unshift({ value: tipo.interfaceAutorizacaoId, label: 'Interface atual' })
  }

  return (
    <>
      <form className={styles.form} onSubmit={handleSubmit((dados) => setDadosPendentes(dados))} noValidate>
        <div className={styles.grid}>
          <FormField label="Nome amigável" htmlFor="nomeAmigavel" required error={errors.nomeAmigavel?.message}>
            <Input
              id="nomeAmigavel"
              invalid={Boolean(errors.nomeAmigavel)}
              disabled={!podeEditar}
              {...register('nomeAmigavel')}
            />
          </FormField>

          <FormField label="Código na operadora" htmlFor="codigoOperadora" error={errors.codigoOperadora?.message}>
            <Input
              id="codigoOperadora"
              invalid={Boolean(errors.codigoOperadora)}
              disabled={!podeEditar}
              {...register('codigoOperadora')}
            />
          </FormField>

          <FormField
            label="Operadora"
            htmlFor="operadorasId"
            required
            error={errors.operadorasId?.message}
          >
            <Controller
              control={control}
              name="operadorasId"
              render={({ field }) => (
                <LookupSelect
                  id="operadorasId"
                  termo={termoOperadora}
                  onTermoChange={setTermoOperadora}
                  itens={operadoras.data ?? []}
                  isLoading={operadoras.isFetching}
                  selecionado={operadoraEscolhida}
                  disabled={!podeEditar}
                  placeholder="Preencha o nome ou o registro ANS"
                  onSelecionar={(item) => {
                    setOperadoraEscolhida(item)
                    field.onChange(item?.id ?? '')
                  }}
                />
              )}
            />
          </FormField>

          <FormField
            label="Interface de autorização"
            htmlFor="interfaceAutorizacaoId"
            required
            error={errors.interfaceAutorizacaoId?.message}
            hint="Qual crawler atende esse tipo."
          >
            <Controller
              control={control}
              name="interfaceAutorizacaoId"
              render={({ field }) => (
                <Select
                  id="interfaceAutorizacaoId"
                  options={opcoesInterface}
                  isLoading={interfaces.isFetching}
                  disabled={!podeEditar}
                  invalid={Boolean(errors.interfaceAutorizacaoId)}
                  value={field.value}
                  onChange={field.onChange}
                />
              )}
            />
          </FormField>
        </div>

        <div className={styles.grid}>
          <Checkbox id="hasCpf" label="Exige CPF" disabled={!podeEditar} {...register('hasCpf')} />
          <Checkbox id="hasCnpj" label="Exige CNPJ" disabled={!podeEditar} {...register('hasCnpj')} />
          <Checkbox
            id="hasTipoLogin"
            label="Exige tipo de login"
            disabled={!podeEditar}
            {...register('hasTipoLogin')}
          />
          <Checkbox
            id="hasUnidadeLogin"
            label="Exige unidade de login"
            disabled={!podeEditar}
            {...register('hasUnidadeLogin')}
          />
          <Checkbox
            id="hasEligibility"
            label="Tem elegibilidade"
            disabled={!podeEditar}
            {...register('hasEligibility')}
          />
          <Checkbox
            id="hasCancellation"
            label="Tem cancelamento"
            disabled={!podeEditar}
            {...register('hasCancellation')}
          />
          <Checkbox
            id="authThenExec"
            label="Autoriza antes de executar"
            disabled={!podeEditar}
            {...register('authThenExec')}
          />
          <Checkbox
            id="authorizationInfoAfterExecution"
            label="Dados da autorização após a execução"
            disabled={!podeEditar}
            {...register('authorizationInfoAfterExecution')}
          />
          <Checkbox
            id="exibirListaGuiaPrincipal"
            label="Exibir lista na guia principal"
            disabled={!podeEditar}
            {...register('exibirListaGuiaPrincipal')}
          />
          <Checkbox
            id="exibirTesteValidacao"
            label="Exibir teste de validação"
            disabled={!podeEditar}
            {...register('exibirTesteValidacao')}
          />
        </div>

        <div className={styles.grid}>
          <FormField
            label="Procedimentos por guia"
            htmlFor="limitOfProceduresPerRequest"
            error={errors.limitOfProceduresPerRequest?.message}
            hint="Deixe vazio para não ter limite. Número declarado por engano quebra o pedido em várias guias."
          >
            <Input
              id="limitOfProceduresPerRequest"
              type="number"
              min={1}
              disabled={!podeEditar}
              invalid={Boolean(errors.limitOfProceduresPerRequest)}
              {...register('limitOfProceduresPerRequest', { valueAsNumber: true })}
            />
          </FormField>

          <FormField
            label="Limite de dias para atualização"
            htmlFor="updateLimitDays"
            required
            error={errors.updateLimitDays?.message}
          >
            <Input
              id="updateLimitDays"
              type="number"
              min={0}
              disabled={!podeEditar}
              invalid={Boolean(errors.updateLimitDays)}
              {...register('updateLimitDays', { valueAsNumber: true })}
            />
          </FormField>

          <FormField
            label="Validade da assinatura em dias"
            htmlFor="validadeAssinaturaDias"
            error={errors.validadeAssinaturaDias?.message}
          >
            <Input
              id="validadeAssinaturaDias"
              type="number"
              min={0}
              disabled={!podeEditar}
              invalid={Boolean(errors.validadeAssinaturaDias)}
              {...register('validadeAssinaturaDias', { valueAsNumber: true })}
            />
          </FormField>

          <FormField label="Formato da biometria facial" htmlFor="formatoCapturaBiometriaFacial">
            <Select
              id="formatoCapturaBiometriaFacial"
              options={FORMATO_BIOMETRIA_OPTIONS}
              disabled={!podeEditar}
              {...register('formatoCapturaBiometriaFacial')}
            />
          </FormField>
        </div>

        <FormField label="URL do portal" htmlFor="urlPortal">
          <Input id="urlPortal" disabled={!podeEditar} {...register('urlPortal')} />
        </FormField>

        {podeEditar ? (
          <div className={styles.actions}>
            <Button type="submit" disabled={Boolean(tipo) && !isDirty}>
              {tipo ? 'Salvar alterações' : 'Criar tipo de credencial'}
            </Button>
          </div>
        ) : null}
      </form>

      <ConfirmDialog
        isOpen={Boolean(dadosPendentes)}
        toggle={() => setDadosPendentes(null)}
        title={tipo ? 'Confirmar alteração do tipo' : 'Confirmar novo tipo de credencial'}
        message={
          tipo
            ? 'Essa alteração vale para todas as credenciais desse tipo, com efeito imediato. Deseja continuar?'
            : 'O tipo passa a ficar disponível para novas credenciais. Deseja continuar?'
        }
        confirmLabel={tipo ? 'Salvar alterações' : 'Criar tipo de credencial'}
        tone="warning"
        isLoading={salvando}
        onConfirm={() => {
          if (!dadosPendentes) return
          const payload = montarPayload(dadosPendentes, tipo)
          if (tipo) atualizar.mutate(payload)
          else criar.mutate(payload)
        }}
      />
    </>
  )
}

const TiposCredenciaisDetail = () => {
  const { tipoCredencialId } = useParams<{ tipoCredencialId: string }>()
  const navigate = useNavigate()
  const criando = tipoCredencialId === 'novo'
  const [abaAtiva, setAbaAtiva] = useState('configuracao')

  const { data: tipo, isPending } = useTipoCredencialQuery(criando ? undefined : tipoCredencialId)

  if (criando) {
    return (
      <>
        <PageHeader title="Novo tipo de credencial" backTo="/tipos-credenciais" />
        <TipoCredencialForm
          tipo={null}
          onCriado={(id) => navigate(id ? `/tipos-credenciais/${id}` : '/tipos-credenciais')}
        />
      </>
    )
  }

  if (isPending) {
    return (
      <>
        <PageHeader title="Tipo de credencial" backTo="/tipos-credenciais" />
        <Skeleton variant="text" count={6} />
      </>
    )
  }

  if (!tipo) {
    return (
      <>
        <PageHeader title="Tipo de credencial" backTo="/tipos-credenciais" />
        <EmptyState
          title="Tipo de credencial inexistente"
          description="Ele pode ter sido removido ou o identificador está incorreto."
        />
      </>
    )
  }

  const abas = [
    {
      id: 'configuracao',
      label: 'Configuração',
      content: <TipoCredencialForm tipo={tipo} onCriado={() => undefined} />,
    },
    {
      id: 'credenciais',
      label: 'Credenciais deste tipo',
      content: <CredenciaisDoTipo tipoCredencialId={tipo.id ?? ''} />,
    },
  ]

  return (
    <>
      <PageHeader
        title={orDash(tipo.nomeAmigavel)}
        subtitle={
          <span className={styles.subtitle}>
            <span>
              {orDash(tipo.operadoras?.nomeFantasia ?? tipo.operadoras?.razaoSocial)}
            </span>
            <span className={styles.identificador}>ID {orDash(tipo.id)}</span>
          </span>
        }
        backTo="/tipos-credenciais"
      />

      <DefinitionList
        items={[
          { label: 'Registro ANS', value: orDash(tipo.operadoras?.registroAns) },
          { label: 'Código na operadora', value: orDash(tipo.codigoOperadora) },
        ]}
      />

      <Tabs items={abas} activeId={abaAtiva} onChange={setAbaAtiva} />
    </>
  )
}

export default TiposCredenciaisDetail
